/**
 * Last known officer GPS fix, readable outside React.
 *
 * `useOfficerLocationShare` owns the geolocation watch and publishes each fix to
 * the LiveKit data topic `officer_gps`, but it keeps `lastFix` in component state
 * — so only `OfficerLocationToggle` can see it. The geocode thunk runs in Redux,
 * outside the component tree, and needs the officer's position to bias a lookup
 * when the caller has not shared theirs.
 *
 * A module-level cache is deliberate rather than a new Redux slice: this is a
 * transient device reading, not application state worth persisting, rehydrating
 * or time-travelling. Putting it in the store would also mean a reducer change in
 * a file several other sessions are editing tonight.
 *
 * ⚠ Read this as "best known", never as "current". It is only ever used to BIAS a
 * geocode; `lib/geo/anchor.ts` re-validates age and accuracy before trusting it,
 * and a missing value simply means no proximity is sent.
 */

import type { GeoFix } from './anchor';

let lastOfficerFix: GeoFix | null = null;

/** Record a fix. Called from the geolocation watch on every update. */
export function setOfficerFix(
  lat: number,
  lng: number,
  accuracy?: number,
): void {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  lastOfficerFix = {
    latitude: lat,
    longitude: lng,
    accuracy: Number.isFinite(accuracy as number) ? (accuracy as number) : null,
    // Stamped here rather than trusting a caller-supplied time, so the staleness
    // check in anchor.ts is measured against when WE observed it.
    capturedAt: Date.now(),
  };
}

/** Forget the fix — call when sharing is turned off, so it cannot go stale-but-used. */
export function clearOfficerFix(): void {
  lastOfficerFix = null;
}

export function getOfficerFix(): GeoFix | null {
  return lastOfficerFix;
}
